import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bell } from "lucide-react";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { API_URL, getAuthHeaders, cn } from "../../lib/utils";
import { fmtDateTime } from "./visitorUtils";
import { useVisitorsBase, withVisitorsBase } from "./VisitorBase";

const POLL_MS = 60000;

/** Notificaciones internas (llegada de visitas, aprobaciones, incidentes). */
export const NotificationBell = ({ className, side = "top", align = "start" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const base = useVisitorsBase();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/notifications`, { headers: getAuthHeaders(), params: { limit: 20 } });
      setData(r.data);
    } catch {
      // Sin conexión o backend sin el módulo: la campana simplemente queda vacía
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const openItem = async (n) => {
    if (!n.read) {
      await axios.post(`${API_URL}/notifications/${n.id}/read`, {}, { headers: getAuthHeaders() }).catch(() => {});
    }
    setOpen(false);
    load();
    if (n.link) navigate(withVisitorsBase(n.link, base));
  };

  const markAll = async () => {
    await axios.post(`${API_URL}/notifications/read-all`, {}, { headers: getAuthHeaders() }).catch(() => {});
    load();
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) load();
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className={cn("relative h-9 w-9", className)} aria-label={t("visitors.notifications.title")}>
          <Bell className="h-5 w-5" />
          {data.unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
              {data.unread > 99 ? "99+" : data.unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} side={side} className="w-80 p-0">
        <div className="flex items-center justify-between px-4 py-2 border-b">
          <p className="font-semibold text-sm">{t("visitors.notifications.title")}</p>
          {data.unread > 0 && (
            <button className="text-xs text-primary hover:underline" onClick={markAll}>
              {t("visitors.notifications.markAllRead")}
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto divide-y">
          {data.items.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6">{t("visitors.notifications.empty")}</p>
          ) : (
            data.items.map((n) => (
              <button
                key={n.id}
                onClick={() => openItem(n)}
                className={cn("w-full text-left px-4 py-2 hover:bg-slate-50", !n.read && "bg-blue-50/60")}
              >
                <p className="text-sm font-medium">{n.title}</p>
                <p className="text-xs text-slate-600">{n.message}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">{fmtDateTime(n.created_at)}</p>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
